import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const roleRedirects = { investor: '/investor-page', investee: '/investee-page' };

// normalize: remove trailing slash (except root)
const normalizePath = (p) => {
  if (!p) return '/';
  return p !== '/' && p.endsWith('/') ? p.slice(0, -1) : p;
};

// use a Set for O(1) lookups-I will chnage the route when changed on backend
const criticalPaths = new Set(['/investee-match-test']);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [accessAllowed, setAccessAllowed] = useState(null); // null = loading

  const token = localStorage.getItem('token');
  const userType = localStorage.getItem('user_type');
  const path = normalizePath(location.pathname);

  const isAuthed = !!token && !!userType;
  const roleOK = isAuthed && allowedRoles?.includes(userType);

  // Always run the effect; decide inside what to do (avoids conditional hook warning)
  useEffect(() => {
    let cancel;

    const verifyAccess = async () => {
      // unauthenticated: nothing to check
      if (!isAuthed) { setAccessAllowed(false); return; }

      // role not allowed: nothing to check
      if (!roleOK) { setAccessAllowed(false); return; }

      // only server-check critical pages
      if (!criticalPaths.has(path)) { setAccessAllowed(true); return; }

      try {
        setAccessAllowed(null);
        const res = await axios.get(`/api/check-access${path}`, {
          headers: { Authorization: `Bearer ${token}` },
          cancelToken: new axios.CancelToken((c) => (cancel = c)),
        });
        setAccessAllowed(!!res.data?.allowed);
        if (!res.data?.allowed) navigate(roleRedirects[userType] || '/');
      } catch (err) {
        console.error('check-access failed:', err?.response?.status, err?.message);
        setAccessAllowed(false);
        navigate(roleRedirects[userType] || '/');
      }
    };

    verifyAccess();
    return () => cancel && cancel();
  }, [isAuthed, roleOK, path, token, userType, navigate]);

  // Not authenticated
  if (!isAuthed) return <Navigate to="/login" replace />;

  // Role not allowed
  if (!roleOK) {
    const redirectPath = roleRedirects[userType] || '/';
    if (location.pathname !== redirectPath) return <Navigate to={redirectPath} replace />;
    return <Navigate to="/" replace />;
  }

  // Still checking (for critical paths)
  if (accessAllowed === null) return null;

  // Denied (already redirected)
  if (!accessAllowed) return null;

  return children;
};

export default ProtectedRoute;
